import React, { useState, useEffect } from "react";
import {
  Shield,
  Users,
  RefreshCw,
  UserPlus,
  Mail,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { debugLog } from "../../utils/debugLog";
import { Card } from "../ui/Card";
import { ConfirmDialog } from "../ui/Modal";
import {
  ROLE_TEMPLATES,
  ROLE_STYLES,
  getTemplate,
} from "../../constants/permissionDefaults";

// ── Invite User Form ────────────────────────────────────────────

const InviteUserForm = ({ palette, onSuccess, showMessage }) => {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("director");
  const [sending, setSending] = useState(false);
  const inputStyle = {
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
  };

  const handleInvite = async () => {
    if (!email.trim() || !fullName.trim()) {
      showMessage("Email and full name are required", "error");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showMessage("Please enter a valid email address", "error");
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "user-management",
        {
          body: {
            action: "invite-user",
            email: email.trim(),
            full_name: fullName.trim(),
            role,
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      showMessage(`Invite sent to ${email}`);
      setEmail("");
      setFullName("");
      setRole("director");
      onSuccess?.();
    } catch (err) {
      const msg = err.message || "";
      if (msg.toLowerCase().includes("already") || msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("exists")) {
        showMessage(`A user with email ${email.trim()} already exists`, "error");
      } else {
        showMessage("Invite failed: " + msg, "error");
      }
      debugLog.error("Invite user", err);
    }
    setSending(false);
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <h3
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "0 0 16px",
          fontSize: FONT_SIZES.md,
          fontWeight: 600,
          color: palette.text1,
          paddingBottom: 12,
          borderBottom: `1px solid ${palette.cardBorder}`,
        }}
      >
        <UserPlus size={ICON_SIZES.md} style={{ color: palette.accent1 }} /> Invite New
        User
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name (e.g. Jane Smith)"
            style={{ ...inputStyle, flex: "1 1 180px" }}
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            type="email"
            style={{ ...inputStyle, flex: "1 1 220px" }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleInvite();
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{ ...inputStyle, minWidth: 180 }}
          >
            {ROLE_TEMPLATES.filter((t) => !t.locked).map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleInvite}
            disabled={sending}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: sending ? palette.text3 : palette.accent1,
              color: "#fff",
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              fontFamily: FONT_FAMILY,
              cursor: sending ? "wait" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <UserPlus size={ICON_SIZES.sm} /> {sending ? "Sending..." : "Send Invite"}
          </button>
        </div>
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
            lineHeight: 1.5,
          }}
        >
          The user will receive an email invitation to set their password and
          access the system.
        </div>
      </div>
    </Card>
  );
};

// ── Users Panel ─────────────────────────────────────────────────

export const UsersPanel = ({ palette }) => {
  const { isAdmin, showMessage, members, currentUser } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from("profiles")
        .select("id, full_name, role, created_at, last_active_at")
        .order("role", { ascending: true })
        .order("full_name", { ascending: true });
      if (e) throw e;
      setUsers(data || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const getAssignedCount = (name) =>
    members.filter((m) => m.assignedDirector === name).length;
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  const getPresenceStatus = (lastActive) => {
    if (!lastActive)
      return { label: "Never", color: "#9ca3af", dot: "#9ca3af" };
    const ms = Date.now() - new Date(lastActive).getTime();
    const mins = ms / 60000;
    const hours = mins / 60;
    const days = hours / 24;
    if (mins < 10)
      return { label: "Online now", color: "#16a34a", dot: "#10b981" };
    if (hours < 1)
      return {
        label: `${Math.round(mins)}m ago`,
        color: "#16a34a",
        dot: "#10b981",
      };
    if (hours < 24)
      return {
        label: `${Math.round(hours)}h ago`,
        color: "#d97706",
        dot: "#f59e0b",
      };
    if (days < 7)
      return {
        label: `${Math.round(days)}d ago`,
        color: "#9ca3af",
        dot: "#9ca3af",
      };
    return { label: formatDate(lastActive), color: "#9ca3af", dot: "#9ca3af" };
  };

  const handleDelete = async (user) => {
    setActionLoading(user.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "user-management",
        { body: { action: "delete-user", user_id: user.id } }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      showMessage(`${user.full_name} removed`);
      setConfirmDelete(null);
      loadUsers();
    } catch (err) {
      showMessage("Delete failed: " + err.message, "error");
    }
    setActionLoading(null);
  };

  const handleResetPassword = async (user) => {
    const userEmail = prompt(
      `Enter the email address for ${user.full_name} to send a password reset:`
    );
    if (!userEmail) return;
    setActionLoading(user.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "user-management",
        { body: { action: "reset-password", email: userEmail } }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      showMessage(`Password reset sent for ${user.full_name}`);
    } catch (err) {
      showMessage("Reset failed: " + err.message, "error");
    }
    setActionLoading(null);
  };

  const handleRoleChange = async (user, newRole) => {
    if (newRole === user.role) return;
    const oldLabel = ROLE_STYLES[user.role]?.label || user.role;
    const newLabel = ROLE_STYLES[newRole]?.label || newRole;
    const oldTemplate = getTemplate(user.role);
    const newTemplate = getTemplate(newRole);
    const tierChange =
      oldTemplate && newTemplate && oldTemplate.dbTier !== newTemplate.dbTier;
    let confirmMsg = `Change ${user.full_name} from ${oldLabel} to ${newLabel}?`;
    if (tierChange) {
      confirmMsg += `\n\nDatabase tier changes from ${oldTemplate.dbTier} to ${newTemplate.dbTier}.`;
    }
    if (newTemplate?.dbTier === "volunteer" && oldTemplate?.dbTier === "director") {
      confirmMsg +=
        "\nThis will remove their access to gift and donor financial data.";
    }
    if (newRole === "admin") {
      confirmMsg +=
        "\nThis will give them full access including Settings, user management, and permissions.";
    }
    const confirmed = window.confirm(confirmMsg);
    if (!confirmed) return;
    setActionLoading(user.id);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ role: newRole })
        .eq("id", user.id);
      if (error) throw error;
      showMessage(`${user.full_name} changed to ${newLabel}`);
      loadUsers();
    } catch (err) {
      showMessage("Role change failed: " + err.message, "error");
    }
    setActionLoading(null);
  };

  if (!isAdmin) {
    return (
      <Card>
        <div
          style={{
            textAlign: "center",
            padding: 32,
            color: palette.text2,
            fontSize: FONT_SIZES.sm,
          }}
        >
          <Shield size={ICON_SIZES.lg} style={{ color: palette.text3, marginBottom: 8 }} />
          <div>Only administrators can view user accounts.</div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <InviteUserForm
        palette={palette}
        onSuccess={loadUsers}
        showMessage={showMessage}
      />

      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: 0,
              fontSize: FONT_SIZES.md,
              fontWeight: 600,
              color: palette.text1,
            }}
          >
            <Users size={ICON_SIZES.md} style={{ color: palette.accent1 }} /> Registered
            Accounts
          </h3>
          <button
            onClick={loadUsers}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 6,
              border: `1px solid ${palette.cardBorder}`,
              background: "transparent",
              color: palette.text2,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              cursor: loading ? "wait" : "pointer",
            }}
          >
            <RefreshCw
              size={ICON_SIZES.xs}
              style={{
                animation: loading ? "spin 1s linear infinite" : "none",
              }}
            />{" "}
            Refresh
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fca5a5",
              color: "#991b1b",
              fontSize: FONT_SIZES.sm,
              marginBottom: 16,
            }}
          >
            Error loading users: {error}
          </div>
        )}

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: 32,
              color: palette.text3,
              fontSize: FONT_SIZES.sm,
            }}
          >
            Loading accounts...
          </div>
        ) : users.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 32,
              color: palette.text3,
              fontSize: FONT_SIZES.sm,
            }}
          >
            No user accounts found.
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              {[
                {
                  value: users.length,
                  label: "Total Accounts",
                  color: palette.accent1,
                },
                {
                  value: users.filter((u) => u.role === "admin").length,
                  label: "Admins",
                  color: "#991b1b",
                },
                {
                  value: users.filter((u) => u.role === "director").length,
                  label: "Directors",
                  color: "#7c3aed",
                },
                {
                  value: users.filter((u) => u.role === "event_manager").length,
                  label: "Event Mgrs",
                  color: "#0d9488",
                },
                {
                  value: users.filter((u) => u.role === "gift_processor").length,
                  label: "Gift Proc.",
                  color: "#b45309",
                },
                {
                  value: users.filter((u) => u.role === "data_analyst").length,
                  label: "Analysts",
                  color: "#2563eb",
                },
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 8,
                    background: `${s.color}08`,
                    border: `1px solid ${s.color}20`,
                    textAlign: "center",
                    flex: "1 1 100px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: 700,
                      color: s.color,
                    }}
                  >
                    {s.value}
                  </div>
                  <div
                    style={{ fontSize: FONT_SIZES.xs, color: palette.text2 }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {users.map((user) => {
                const rs = ROLE_STYLES[user.role] || {
                  bg: "#f9fafb",
                  color: "#374151",
                  border: "#d1d5db",
                  label: user.role || "Unknown",
                };
                const assignedCount = getAssignedCount(user.full_name);
                const isMe = user.id === currentUser?.id;
                const isActing = actionLoading === user.id;
                const presence = getPresenceStatus(user.last_active_at);
                const initials = user.full_name
                  ? user.full_name
                      .split(/\s+/)
                      .map((w) => w[0])
                      .join("")
                      .substring(0, 2)
                      .toUpperCase()
                  : "?";

                return (
                  <div
                    key={user.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 8,
                      border: `1px solid ${
                        isMe ? palette.accent1 + "40" : palette.cardBorder
                      }`,
                      background: isMe
                        ? `${palette.accent1}04`
                        : palette.inputBg,
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: isMe
                          ? palette.accent1
                          : `${palette.accent1}80`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 14,
                        fontFamily: FONT_FAMILY,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: FONT_SIZES.sm,
                            fontWeight: 700,
                            color: palette.text1,
                          }}
                        >
                          {user.full_name || "Unnamed User"}
                        </span>
                        {isMe ? (
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: 10,
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 4,
                              background: rs.bg,
                              color: rs.color,
                              border: `1px solid ${rs.border}`,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {rs.label}
                          </span>
                        ) : (
                          <select
                            value={user.role}
                            onChange={(e) =>
                              handleRoleChange(user, e.target.value)
                            }
                            disabled={isActing}
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: rs.bg,
                              color: rs.color,
                              border: `1px solid ${rs.border}`,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              cursor: isActing ? "wait" : "pointer",
                              fontFamily: FONT_FAMILY,
                              outline: "none",
                              appearance: "auto",
                            }}
                          >
                            {ROLE_TEMPLATES.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {isMe && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: palette.accent1,
                            }}
                          >
                            (you)
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: FONT_SIZES.xs,
                          color: palette.text3,
                          display: "flex",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <span>Created: {formatDate(user.created_at)}</span>
                        {(user.role === "director" || user.role === "volunteer") && (
                          <span>
                            {assignedCount > 0
                              ? `${assignedCount} donor${
                                  assignedCount !== 1 ? "s" : ""
                                } assigned`
                              : "No donors assigned"}
                          </span>
                        )}
                      </div>
                    </div>

                    {!isMe && (
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => handleResetPassword(user)}
                          disabled={isActing}
                          title="Send password reset"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "6px 10px",
                            borderRadius: 6,
                            border: `1px solid ${palette.cardBorder}`,
                            background: "transparent",
                            color: palette.text2,
                            fontSize: FONT_SIZES.xs,
                            fontWeight: 600,
                            fontFamily: FONT_FAMILY,
                            cursor: isActing ? "wait" : "pointer",
                            opacity: isActing ? 0.5 : 1,
                          }}
                        >
                          <Mail size={ICON_SIZES.xs} /> Reset
                        </button>
                        <button
                          onClick={() => setConfirmDelete(user)}
                          disabled={isActing}
                          title="Delete user"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "6px 10px",
                            borderRadius: 6,
                            border: "1px solid #fca5a5",
                            background: "#fef2f2",
                            color: "#991b1b",
                            fontSize: FONT_SIZES.xs,
                            fontWeight: 600,
                            fontFamily: FONT_FAMILY,
                            cursor: isActing ? "wait" : "pointer",
                            opacity: isActing ? 0.5 : 1,
                          }}
                        >
                          <Trash2 size={ICON_SIZES.xs} /> Delete
                        </button>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 2,
                        flexShrink: 0,
                        minWidth: 70,
                      }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: presence.dot,
                          boxShadow:
                            presence.label === "Online now"
                              ? `0 0 0 2px ${presence.dot}30`
                              : "none",
                        }}
                      />
                      <span
                        style={{
                          fontSize: 10,
                          color: presence.color,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {presence.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete User Account?"
          message={
            <>
              Remove <strong>{confirmDelete.full_name}</strong> (
              {confirmDelete.role})?
              {getAssignedCount(confirmDelete.full_name) > 0 && (
                <>
                  <br />
                  <br />
                  <span style={{ color: "#dc2626" }}>
                    <AlertTriangle
                      size={ICON_SIZES.sm}
                      style={{ verticalAlign: "middle", marginRight: 4 }}
                    />
                    {getAssignedCount(confirmDelete.full_name)} assigned
                    donor(s) will be unassigned.
                  </span>
                </>
              )}
              <br />
              <br />
              This action cannot be undone.
            </>
          }
          confirmLabel="Delete User"
          variant="danger"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
};
